"use strict";
/*
This file is part of web3.js.

web3.js is free software: you can redistribute it and/or modify
it under the terms of the GNU Lesser General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

web3.js is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Lesser General Public License for more details.

You should have received a copy of the GNU Lesser General Public License
along with web3.js.  If not, see <http://www.gnu.org/licenses/>.
*/
Object.defineProperty(exports, "__esModule", { value: true });
exports.ProviderCapabilityError = exports.RequestQueueFullError = exports.RequestTimeoutError = exports.Web3WSProviderError = exports.SubscriptionError = exports.InvalidClientError = exports.InvalidProviderError = exports.ProviderError = void 0;
/* eslint-disable max-classes-per-file */
const error_codes_js_1 = require("../error_codes.js");
const web3_error_base_js_1 = require("../web3_error_base.js");
class ProviderError extends web3_error_base_js_1.BaseWeb3Error {
    constructor() {
        super(...arguments);
        this.code = error_codes_js_1.ERR_PROVIDER;
    }
}
exports.ProviderError = ProviderError;
class InvalidProviderError extends web3_error_base_js_1.BaseWeb3Error {
    constructor(clientUrl) {
        super(`Provider with url "${clientUrl}" is not set or invalid`);
        this.clientUrl = clientUrl;
        this.code = error_codes_js_1.ERR_INVALID_PROVIDER;
    }
}
exports.InvalidProviderError = InvalidProviderError;
class InvalidClientError extends web3_error_base_js_1.BaseWeb3Error {
    constructor(clientUrl) {
        super(`Client URL "${clientUrl}" is invalid.`);
        this.code = error_codes_js_1.ERR_INVALID_CLIENT;
    }
}
exports.InvalidClientError = InvalidClientError;
class SubscriptionError extends web3_error_base_js_1.BaseWeb3Error {
    constructor() {
        super(...arguments);
        this.code = error_codes_js_1.ERR_SUBSCRIPTION;
    }
}
exports.SubscriptionError = SubscriptionError;
class Web3WSProviderError extends web3_error_base_js_1.BaseWeb3Error {
    constructor() {
        super(...arguments);
        this.code = error_codes_js_1.ERR_WS_PROVIDER; // this had duplicate code with generic provider
    }
}
exports.Web3WSProviderError = Web3WSProviderError;
/**
 * Thrown when a JSON-RPC request exceeds its total deadline (`requestTimeout`).
 *
 * This covers the whole request lifetime, including any time spent waiting for a
 * connection to become available. It is distinct from {@link ConnectionTimeoutError},
 * which only bounds the connect/handshake phase.
 */
class RequestTimeoutError extends ProviderError {
    constructor(duration, method) {
        super(`REQUEST TIMEOUT: request${method ? ` "${method}"` : ''} exceeded the ${duration}ms deadline`);
        this.duration = duration;
        this.method = method;
        this.code = error_codes_js_1.ERR_REQUEST_TIMEOUT;
    }
    toJSON() {
        return Object.assign(Object.assign({}, super.toJSON()), { duration: this.duration, method: this.method });
    }
}
exports.RequestTimeoutError = RequestTimeoutError;
/**
 * Thrown when a provider already has `maxInFlightRequests` requests outstanding.
 *
 * This error is retryable: the condition is transient backpressure, not a bad request.
 * Callers should back off and retry rather than treating it as a permanent failure.
 */
class RequestQueueFullError extends ProviderError {
    constructor(maxInFlightRequests) {
        super(`REQUEST QUEUE FULL: provider already has ${maxInFlightRequests} in-flight requests (maxInFlightRequests). Retry after pending requests settle.`);
        this.maxInFlightRequests = maxInFlightRequests;
        this.code = error_codes_js_1.ERR_REQUEST_QUEUE_FULL;
        this.retryable = true;
    }
    toJSON() {
        return Object.assign(Object.assign({}, super.toJSON()), { maxInFlightRequests: this.maxInFlightRequests, retryable: this.retryable });
    }
}
exports.RequestQueueFullError = RequestQueueFullError;
/**
 * Thrown when the runtime or an injected dependency cannot provide a capability that
 * is required to enforce a safety bound, so the provider fails closed rather than
 * silently dropping the bound.
 *
 * The motivating case: a custom `fetch` returns a non-empty response with no readable
 * body stream, which makes it impossible to enforce `maxResponseBytes` incrementally.
 * Falling back to an unbounded `response.text()` would defeat the bound entirely.
 */
class ProviderCapabilityError extends ProviderError {
    constructor(capability, reason) {
        super(`PROVIDER CAPABILITY MISSING: "${capability}" is required but unavailable${reason ? `: ${reason}` : ''}. Refusing to continue without it.`);
        this.capability = capability;
        this.code = error_codes_js_1.ERR_PROVIDER_CAPABILITY;
    }
    toJSON() {
        return Object.assign(Object.assign({}, super.toJSON()), { capability: this.capability });
    }
}
exports.ProviderCapabilityError = ProviderCapabilityError;
//# sourceMappingURL=provider_errors.js.map